import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './MyJobsQuotations.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[code] || '$';
};

const MyJobsQuotations = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const socket = useSocket();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showQuotationsModal, setShowQuotationsModal] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [acceptingOffer, setAcceptingOffer] = useState(null);

  const quoteRefs = useRef({});

  const fetchJobs = async () => {
    try {
      setLoading(true);

      const storedToken = token || localStorage.getItem('marilink_token');

      const rfqsRes = await fetch(`${API_BASE}/rfqs`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      const rfqsData = await rfqsRes.json();

      const bidsRes = await fetch(`${API_BASE}/bids`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      const bidsData = await bidsRes.json();

      const contractsRes = await fetch(`${API_BASE}/contracts`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      const contractsData = await contractsRes.json();
      const contracts = contractsData.contracts || [];

      if (rfqsData.success) {
        const enrichedJobs = (rfqsData.rfqs || []).map(rfq => {
          const jobBids = (bidsData.bids || []).filter(b => b.rfqId === rfq._id);
          const pendingBids = jobBids.filter(b => b.status === 'pending');
          const revisedBids = jobBids.filter(b => b.status === 'revised');
          const acceptedBid = jobBids.find(b => b.status === 'accepted');
          const lowestBid = pendingBids.length ? Math.min(...pendingBids.map(b => b.amount)) : null;
          const hasAcceptedBid = !!acceptedBid;
          const hasRevisedBid = revisedBids.length > 0;
          const relatedContract = contracts.find(c => c.rfqId === rfq._id);

          return {
            id: rfq._id,
            vessel: rfq.vesselName,
            service: rfq.serviceType,
            status: rfq.status,
            quotations: hasAcceptedBid ? 0 : pendingBids.length,
            lowestBid: lowestBid,
            hasAcceptedBid: hasAcceptedBid,
            hasRevisedBid: hasRevisedBid,
            contractId: relatedContract?._id || null,
            acceptedBidAmount: acceptedBid?.amount || null,
            acceptedVendor: acceptedBid?.vendorName || null,
            currency: rfq.currency || 'USD'
          };
        });

        setJobs(enrichedJobs);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchQuotationsForJob = async (rfqId) => {
    setQuotationsLoading(true);
    setQuotations([]);

    try {
      const storedToken = token || localStorage.getItem('marilink_token');

      const response = await fetch(`${API_BASE}/bids/job/${rfqId}`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });

      const data = await response.json();

      if (data.success) {
        const enriched = await Promise.all(
          data.bids.map(async (bid) => {
            if (bid.vendorId && !bid.vendorCompany) {
              try {
                const vendorRes = await fetch(`${API_BASE}/users/${bid.vendorId}`, {
                  headers: { Authorization: `Bearer ${storedToken}` }
                });
                const vendorData = await vendorRes.json();
                if (vendorData.success) {
                  bid.vendorCompany = vendorData.user?.company || vendorData.user?.name;
                }
              } catch (e) {}
            }
            return bid;
          })
        );

        setQuotations(enriched);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const handleViewQuotations = (job) => {
    setSelectedJob(job);
    setShowQuotationsModal(true);
    fetchQuotationsForJob(job.id);
  };

  const handleAcceptQuotation = async (quotation) => {
    const isRevised = quotation.isRevision === true;
    const revisedAmount = quotation.amount;

    let confirmMsg = `Accept quotation from ${quotation.vendorCompany || quotation.vendorName || 'Vendor'} for`;

    if (isRevised) {
      confirmMsg += `\n\nRevised Amount: ${getCurrencySymbol(quotation.currency)}${revisedAmount.toLocaleString()} ${quotation.currency || 'USD'}\n\nAccept?`;
    } else {
      confirmMsg += ` ${getCurrencySymbol(quotation.currency)}${revisedAmount?.toLocaleString()} ${quotation.currency || 'USD'}?`;
    }

    if (!window.confirm(confirmMsg)) return;

    setAcceptingOffer(quotation._id);

    try {
      const storedToken = token || localStorage.getItem('marilink_token');

      const response = await fetch(`${API_BASE}/contracts/accept-bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`
        },
        body: JSON.stringify({
          rfqId: selectedJob.id,
          bidId: quotation._id
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchJobs();
        setShowQuotationsModal(false);
        if (data.contract && data.contract._id) {
          navigate(`/owner/contract/${data.contract._id}`);
        }
      } else {
        alert(data.message);
      }
    } catch (err) {
    } finally {
      setAcceptingOffer(null);
    }
  };

  const handleViewProfile = (vendorId) => {
    setShowQuotationsModal(false);
    navigate(`/owner/vendor/${vendorId}`);
  };

  const handleMessageVendor = (vendorId, vendorName) => {
    setShowQuotationsModal(false);
    navigate(`/messages/${vendorId}`, { state: { userName: vendorName } });
  };

  const handleEditJob = (jobId) => {
    navigate(`/owner/edit-job/${jobId}`);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job? This will remove all associated quotations.')) return;

    try {
      const storedToken = token || localStorage.getItem('marilink_token');

      const response = await fetch(`${API_BASE}/rfqs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${storedToken}` }
      });

      const data = await response.json();

      if (data.success) {
        fetchJobs();
        if (showQuotationsModal && selectedJob?.id === jobId) {
          setShowQuotationsModal(false);
        }
      }
    } catch (err) {}
  };

  const handleDownloadPDF = async (quoteId) => {
    const invoiceElement = quoteRefs.current[quoteId];
    if (!invoiceElement || downloadingId) return;

    setDownloadingId(quoteId);

    try {
      const canvas = await html2canvas(invoiceElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png', 1.0);

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`Quotation_${quoteId?.slice(-8)}.pdf`);
    } catch (error) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : 'N/A';

  const getValidTill = (createdAt, validityDays) => {
    if (!createdAt) return 'N/A';
    const d = new Date(createdAt);
    d.setDate(d.getDate() + (validityDays || 30));
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getQuoteNumber = (bid) => {
    if (!bid?._id || !bid?.createdAt) return 'SZ-0001';
    const year = new Date(bid.createdAt).getFullYear();
    const timestampHex = bid._id.toString().substring(0, 8);
    const timestampDecimal = parseInt(timestampHex, 16);
    const serial = timestampDecimal.toString().slice(-4).padStart(4, '0');
    return `SZ-${year}-${serial}`;
  };

  const filteredJobs = jobs.filter(job =>
    job.vessel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loader-container">
        <i className="fa-solid fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <div className="mjq-wrapper">
      <div className="mjq-header">
        <div className="mjq-header-text">
          <h1>Review Quotations</h1>
          <p>Manage your active jobs and incoming offers</p>
        </div>
        <button className="mjq-btn-primary" onClick={() => navigate('/owner/post-job')}>
          <i className="fa-solid fa-plus"></i> Post a Job
        </button>
      </div>

      <div className="mjq-controls">
        <div className="mjq-search">
          <i className="fa-solid fa-search"></i>
          <input
            type="text"
            placeholder="Search vessel or service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="mjq-count">{filteredJobs.length} records found</div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="mjq-empty">
          <i className="fa-solid fa-folder-open"></i>
          <h3>No records found</h3>
          <p>Post a new job to start receiving quotations.</p>
        </div>
      ) : (
        <div className="mjq-table-container">
          <table className="mjq-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Details</th>
                <th>Quotations</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => {
                const currencySymbol = getCurrencySymbol(job.currency);
                return (
                  <tr key={job.id}>
                    <td className="mjq-job-id">#{job.id?.slice(-8)}</td>
                    <td>
                      <span className="mjq-vessel-name">{job.vessel}</span>
                      <span className="mjq-service">{job.service}</span>
                    </td>
                    <td>
                      <div className="mjq-lead">
                        {job.hasAcceptedBid ? (
                          <span className="mjq-lead-acc">
                            <i className="fa-solid fa-check-circle"></i>
                            {' '}Accepted: {currencySymbol}{job.acceptedBidAmount?.toLocaleString()}
                          </span>
                        ) : (
                          <>
                            <span className="mjq-lead-pend">
                              <i className="fa-solid fa-file-invoice"></i>
                              {' '}{job.quotations} Offers
                            </span>
                            {job.lowestBid && (
                              <span className="mjq-lead-lowest">
                                From {currencySymbol}{job.lowestBid?.toLocaleString()}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`mjq-status ${job.status === 'open' ? 'open' : job.status === 'bidding' ? 'bidding' : 'accepted'}`}>
                        {job.status === 'open' ? 'Open' : job.status === 'bidding' ? 'Reviewing' : 'Contracted'}
                      </span>
                    </td>
                    <td>
                      <div className="mjq-actions">
                        <button className="mjq-action-btn" onClick={() => handleViewQuotations(job)}>
                          <i className="fa-regular fa-eye"></i>
                        </button>
                        {!job.hasAcceptedBid && (
                          <button className="mjq-action-btn" onClick={() => handleEditJob(job.id)}>
                            <i className="fa-regular fa-pen-to-square"></i>
                          </button>
                        )}
                        <button className="mjq-action-btn del" onClick={() => handleDeleteJob(job.id)}>
                          <i className="fa-regular fa-trash-can"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showQuotationsModal && selectedJob && (
        <div className="mjq-modal-overlay" onClick={() => setShowQuotationsModal(false)}>
          <div className="mjq-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mjq-modal-header">
              <h2><i className="fa-solid fa-ship"></i> {selectedJob.vessel}</h2>
              <button className="mjq-modal-close" onClick={() => setShowQuotationsModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="mjq-modal-body">
              {quotationsLoading ? (
                <div className="loader-container" style={{ height: '20vh' }}>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                </div>
              ) : quotations.length === 0 ? (
                <div className="mjq-empty" style={{ border: 'none' }}>
                  <i className="fa-solid fa-inbox"></i>
                  <h3>No Quotations</h3>
                  <p>Providers have not submitted quotes yet.</p>
                </div>
              ) : (
                quotations.map((quote) => {
                  const isAccepted = quote.status === 'accepted';
                  const isRevised = quote.isRevision === true;
                  const isPending = quote.status === 'pending';
                  const isRejected = quote.status === 'rejected';

                  let statusText = '';
                  let statusClass = '';

                  if (isAccepted) { statusText = 'ACCEPTED'; statusClass = 'accepted'; }
                  else if (isRejected) { statusText = 'REJECTED'; statusClass = 'rejected'; }
                  else if (isRevised) { statusText = 'REVISED'; statusClass = 'revised'; }
                  else { statusText = 'PENDING'; statusClass = 'pending'; }

                  const items = quote.quotationItems || [];
                  const currencyCode = quote.currency || 'USD';
                  const currencySymbol = getCurrencySymbol(currencyCode);

                  const rows = [];
                  for (let i = 0; i < 8; i++) {
                    if (i < items.length) {
                      const item = items[i];
                      rows.push(
                        <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '14px 12px', textAlign: 'center', color: '#374151' }}>{i + 1}</td>
                          <td style={{ padding: '14px 12px', color: '#374151' }}>{item.description}</td>
                          <td style={{ padding: '14px 12px', textAlign: 'center', color: '#374151' }}>{item.quantity}</td>
                          <td style={{ padding: '14px 12px', textAlign: 'right', color: '#374151' }}>
                            {currencySymbol} {item.unitPrice?.toLocaleString()} {currencyCode}
                          </td>
                          <td style={{ padding: '14px 12px', textAlign: 'right', fontWeight: '700', color: '#374151' }}>
                            {currencySymbol} {(item.quantity * item.unitPrice).toLocaleString()} {currencyCode}
                          </td>
                        </tr>
                      );
                    } else {
                      rows.push(
                        <tr key={`empty-${i}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '14px 12px', textAlign: 'center', color: '#374151' }}>&nbsp;</td>
                          <td style={{ padding: '14px 12px', color: '#374151' }}>&nbsp;</td>
                          <td style={{ padding: '14px 12px', textAlign: 'center', color: '#374151' }}>&nbsp;</td>
                          <td style={{ padding: '14px 12px', textAlign: 'right', color: '#374151' }}>&nbsp;</td>
                          <td style={{ padding: '14px 12px', textAlign: 'right', fontWeight: '700', color: '#374151' }}>&nbsp;</td>
                        </tr>
                      );
                    }
                  }

                  const vendorDisplayName = quote.vendorCompany || quote.vendorName || 'Vendor';

                  return (
                    <div key={quote._id} className="mjq-quote-wrapper">
                      <div className="mjq-quote-status-bar">
                        <span className={statusClass}>{statusText}</span>
                        <span style={{ fontSize: '13px', color: '#ccc' }}>{vendorDisplayName}</span>
                      </div>

                      <div className="mjq-quote-actions">
                        <button
                          className="mjq-btn-danger"
                          onClick={() => handleDownloadPDF(quote._id)}
                          disabled={downloadingId === quote._id}
                        >
                          {downloadingId === quote._id ? (
                            <i className="fa-solid fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fa-solid fa-file-pdf"></i>
                          )}
                          Download PDF
                        </button>
                      </div>

                      <div className="mjq-invoice-paper-wrap">
                        <div
                          ref={(el) => { quoteRefs.current[quote._id] = el; }}
                          className="mjq-invoice-paper"
                        >
                          <div className="mjq-invoice-header">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img
                                src="/seazex_new_logo.png"
                                alt="SeaZex"
                                style={{ width: '44px', height: '44px', marginRight: '12px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div>
                                <div className="mjq-invoice-logo">
                                  <span className="mjq-invoice-logo-sea">Sea</span>
                                  <span className="mjq-invoice-logo-zex">Zex</span>
                                </div>
                                <div className="mjq-invoice-tagline">Maritime Marketplace</div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div className={`mjq-invoice-status ${statusClass}`}>{statusText}</div>
                              <div className="mjq-invoice-number"># {getQuoteNumber(quote)}</div>
                            </div>
                          </div>

                          <div className="mjq-invoice-info-grid">
                            <div className="mjq-invoice-info-block mjq-invoice-info-block-first">
                              <div className="mjq-invoice-block-title">FROM</div>
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Company</span>
                                <span className="mjq-invoice-value">{vendorDisplayName}</span>
                              </div>
                              {quote.vendorRegistrationNo && (
                                <div className="mjq-invoice-row">
                                  <span className="mjq-invoice-label">Reg. No.</span>
                                  <span className="mjq-invoice-value">{quote.vendorRegistrationNo}</span>
                                </div>
                              )}
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Service</span>
                                <span className="mjq-invoice-value">{selectedJob.service}</span>
                              </div>
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Date</span>
                                <span className="mjq-invoice-value">{formatDate(quote.createdAt)}</span>
                              </div>
                              <div className="mjq-invoice-row mjq-invoice-row-last">
                                <span className="mjq-invoice-label">Valid Until</span>
                                <span className="mjq-invoice-value">
                                  {getValidTill(quote.createdAt, quote.validityDays)}
                                </span>
                              </div>
                            </div>

                            <div className="mjq-invoice-info-block">
                              <div className="mjq-invoice-block-title">TO</div>
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Vessel</span>
                                <span className="mjq-invoice-value">{selectedJob.vessel}</span>
                              </div>
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Service</span>
                                <span className="mjq-invoice-value">{selectedJob.service}</span>
                              </div>
                              <div className="mjq-invoice-row">
                                <span className="mjq-invoice-label">Lead Time</span>
                                <span className="mjq-invoice-value">{quote.leadTime || 'Standard'}</span>
                              </div>
                              <div className="mjq-invoice-row mjq-invoice-row-last">
                                <span className="mjq-invoice-label">Status</span>
                                <span className={`mjq-invoice-status-pill ${isAccepted ? 'accepted' : isRevised ? 'revised' : 'pending'}`}>
                                  {isAccepted ? 'Accepted' : isRevised ? 'Revised' : 'Pending'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mjq-invoice-table-wrap">
                            <table className="mjq-invoice-table">
                              <thead>
                                <tr>
                                  <th className="mjq-invoice-th-sno">#</th>
                                  <th>Item Description</th>
                                  <th className="mjq-invoice-th-qty">Qty</th>
                                  <th className="mjq-invoice-th-price">Unit Price</th>
                                  <th className="mjq-invoice-th-total">Total</th>
                                </tr>
                              </thead>
                              <tbody>{rows}</tbody>
                            </table>
                          </div>

                          <div className="mjq-invoice-footer-section">
                            <div className="mjq-invoice-signature-block">
                              <div className="mjq-invoice-signature-label">Authorized Signatory</div>
                              <div className="mjq-invoice-signature-name">
                                {quote.signatureName || '_________________'}
                              </div>
                              <div className="mjq-invoice-signature-title">
                                For and behalf of {vendorDisplayName}
                              </div>
                            </div>

                            <div className="mjq-invoice-totals-block">
                              <div className="mjq-invoice-total-row">
                                <span>Subtotal</span>
                                <span>{currencySymbol} {quote.amount?.toLocaleString()} {currencyCode}</span>
                              </div>
                              <div className="mjq-invoice-total-row grand">
                                <span>Total Amount</span>
                                <span>{currencySymbol} {quote.amount?.toLocaleString()} {currencyCode}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mjq-invoice-terms">
                            <div className="mjq-invoice-terms-heading">Terms & Conditions</div>
                            <div className="mjq-invoice-terms-text">
                              {quote.termsConditions || `Valid for ${quote.validityDays || 30} days.`}
                            </div>
                          </div>

                          <div className="mjq-invoice-footer">
                            <div className="mjq-invoice-footer-text">
                              SeaZex - The Secure Maritime Service Marketplace
                            </div>
                            <div className="mjq-invoice-footer-small">
                              www.seazex.com | support@seazex.com
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mjq-interaction-bar">
                        <button className="mjq-btn-sec" onClick={() => handleViewProfile(quote.vendorId)}>
                          <i className="fa-regular fa-user"></i> View Profile
                        </button>
                        <button className="mjq-btn-sec" onClick={() => handleMessageVendor(quote.vendorId, vendorDisplayName)}>
                          <i className="fa-regular fa-envelope"></i> Message
                        </button>
                        {!isAccepted && !isRejected ? (
                          <button
                            className="mjq-btn-acc"
                            onClick={() => handleAcceptQuotation(quote)}
                            disabled={acceptingOffer === quote._id}
                          >
                            {acceptingOffer === quote._id ? (
                              <i className="fa-solid fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fa-regular fa-circle-check"></i>
                            )}
                            {acceptingOffer === quote._id ? 'Processing...' : 'Accept & Proceed'}
                          </button>
                        ) : isAccepted ? (
                          <button
                            className="mjq-btn-acc"
                            onClick={() => {
                              setShowQuotationsModal(false);
                              navigate(`/owner/contract/${quote.contractId || selectedJob.contractId}`);
                            }}
                          >
                            <i className="fa-solid fa-file-contract"></i> View Contract
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mjq-footer-note">
              <i className="fa-solid fa-shield-halved"></i> Communicate directly with providers to finalize arrangements
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyJobsQuotations;